import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  useTheme,
  Checkbox,
  FormGroup,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HtmlRenderer from "./HtmlRenderer";

const MockMcq = ({
  mcqData = {},
  selectedValue = "",
  handleAnswerChange,
  instructions = "",
  sectionTitle = "",
  questionNumber = null,
  asDropdown = false,
  zoomLevel = 100,
}) => {
  const theme = useTheme();
  const blue = theme.palette.primary.main;
  const red = "#b81e33";
  const green = "#0AAF36";
  const greyBg = theme.palette.action.hover;
  const border = theme.palette.divider;

  const {
    question_id,
    questionId,
    question = "",
    options = [],
    questionType = "",
    isGrouped = false,
    questionIds = [],
    groupedQuestions = [],
  } = mcqData;
  const id = questionId ?? question_id;
  const displayNumber = questionNumber ?? id ?? "?";
  
  // For grouped questions, show range (e.g., "21-22")
  const displayNumberText = isGrouped && questionIds.length > 1 
    ? `${questionIds[0]}-${questionIds[questionIds.length - 1]}`
    : displayNumber;

  const isMultiple =
    /muti|multi|multiple/i.test(questionType || "") ||
    questionType === "mcq_mutiple" ||
    questionType === "mcq_multiple";
  const maxSelections = isGrouped ? questionIds.length : (questionType === "mcq_multiple" ? 2 : null);
  const [limitExceeded, setLimitExceeded] = useState(false);

  // Auto-detect dropdown mode for specific question types
  const shouldBeDropdown = asDropdown || 
    questionType === "map_labelling" || 
    questionType === "matching_information" ||
    questionType === "matching_feature" ||
    questionType === "matching_headings";

  const letterOfIdx = (i) => String.fromCharCode(65 + i);
  const optionText = (opt) => opt?.text ?? opt?.value ?? opt?.label ?? opt;
  const optionValue = (opt, idx) => opt?.key ?? letterOfIdx(idx);

  return (
    <Box sx={{ width: "100%" }}>
      {Boolean(instructions) && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            border: `1px solid ${border}`,
            borderRadius: 1.5,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 28, color: red, mr: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: red }}>
              Instruction
            </Typography>
          </Box>
          <HtmlRenderer
            content={instructions}
            component="div"
            sx={{
              lineHeight: 1.5,
              userSelect: "text",
              cursor: "text",
              fontSize: `calc((${zoomLevel / 100}) * 0.875rem)`,
              whiteSpace: "pre-line",
              "& p": { mb: 1 },
            }}
          />
        </Paper>
      )}

      {Boolean(sectionTitle) && (
        <Typography variant="h6" sx={{ fontWeight: 700, color: blue, mb: 2 }}>
          {sectionTitle}
        </Typography>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${border}`,
          borderRadius: 1.5,
          userSelect: "text",
          "& *": { userSelect: "text" },
        }}
      >
        <Box
          sx={{
            fontWeight: 500,
            marginBottom: "16px",
            fontSize: `calc((${zoomLevel / 100}) * ${typeof window !== 'undefined' && window.innerWidth < 600 ? '1rem' : '1rem'})`,
            lineHeight: 1.6,
            userSelect: "text",
            cursor: "text",
            color: "black",
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
          }}
        >
          <Box
            component="span"
            sx={{
              marginRight: "4px",
              userSelect: "text",
              color: "#9D2235",
              flexShrink: 0,
            }}
          >
            {displayNumberText}.
          </Box>
          <HtmlRenderer
            content={question}
            component="span"
            sx={{ 
              flex: 1, 
              userSelect: "text", 
              cursor: "text", 
              color: "black",
              fontSize: `calc((${zoomLevel / 100}) * ${typeof window !== 'undefined' && window.innerWidth < 600 ? '1rem' : '1rem'})`,
            }}
          />
        </Box>

        {shouldBeDropdown ? (
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={selectedValue}
              onChange={(e) => handleAnswerChange?.(id, e.target.value)}
              sx={{ 
                borderRadius: 2, 
                bgcolor: "#f9f9f9",
                fontSize: `calc((${zoomLevel / 100}) * ${typeof window !== 'undefined' && window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
              }}
              renderValue={(val) => {
                if (!val) return "Select an option";
                // Format TFNG options to show complete words
                if (val === "true") return "True";
                if (val === "false") return "False";
                if (val === "not_given") return "Not Given";
                // Format Yes/No options
                if (val === "yes") return "Yes";
                if (val === "no") return "No";
                // Default formatting
                return val.replace(/_/g, " ");
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: `calc((${zoomLevel / 100}) * ${typeof window !== 'undefined' && window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                    }
                  }
                }
              }}
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {options.map((opt, idx) => {
                // Use the actual text as the value instead of the key
                const textValue = optionText(opt);
                let displayText = textValue;
                // Format the display text for TFNG to show complete words
                if (displayText === "true") {
                  displayText = "True";
                } else if (displayText === "false") {
                  displayText = "False";
                } else if (displayText === "not_given") {
                  displayText = "Not Given";
                } else if (displayText === "yes") {
                  displayText = "Yes";
                } else if (displayText === "no") {
                  displayText = "No";
                } else {
                  // Default formatting: replace underscores with spaces
                  displayText = displayText.replace(/_/g, " ");
                }
                return (
                  <MenuItem key={idx} value={textValue}>
                    {displayText}
                  </MenuItem>
                );
              })}}}
            </Select>
          </FormControl>
        ) : isMultiple ? (
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {options.map((opt, idx) => {
                const value = optionValue(opt, idx);
                const letter = value.toUpperCase();
                const checked = Array.isArray(selectedValue)
                  ? selectedValue.includes(value)
                  : String(selectedValue) === String(value);
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      pl: 2,
                      borderRadius: 1,
                      bgcolor: greyBg,
                      border: checked
                        ? `2px solid ${green}`
                        : `1px solid transparent`,
                      mb: 1.5,
                      "&:hover": { cursor: "pointer", bgcolor: "#eef0f3" },
                    }}
                    onClick={() => {
                      const current = Array.isArray(selectedValue)
                        ? [...selectedValue]
                        : selectedValue
                        ? [String(selectedValue)]
                        : [];
                      const idxOf = current.indexOf(value);
                      if (idxOf === -1) {
                        if (maxSelections && current.length >= maxSelections) {
                          setLimitExceeded(true);
                          setTimeout(() => setLimitExceeded(false), 1800);
                          return;
                        }
                        current.push(value);
                      } else {
                        current.splice(idxOf, 1);
                      }
                      
                      // For grouped questions, save answer to all questionIds
                      if (isGrouped && questionIds.length > 0) {
                        questionIds.forEach(qId => {
                          handleAnswerChange?.(qId, current);
                        });
                      } else {
                        handleAnswerChange?.(id, current);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        fontWeight: 700,
                        color: checked ? "#fff" : "text.primary",
                        bgcolor: checked ? green : "#fff",
                        border: `2px solid ${green}`,
                        userSelect: "none",
                      }}
                    >
                      {checked ? (
                        <CheckIcon sx={{ fontSize: 18, color: "#fff" }} />
                      ) : (
                        letter
                      )}
                    </Box>
                    <HtmlRenderer
                      content={optionText(opt)}
                      component="span"
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.5,
                        userSelect: "text",
                        cursor: "text",
                        flex: 1,
                        pointerEvents: "auto",
                        fontSize: `calc((${zoomLevel / 100}) * ${typeof window !== 'undefined' && window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                      }}
                    />
                    <Checkbox
                      checked={checked}
                      onChange={() => {}}
                      sx={{ display: "none" }}
                    />
                  </Paper>
                );
              })}
              {limitExceeded && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    color="error"
                  >{`You can select up to ${maxSelections} options. Click on selected options to deselect.`}</Typography>
                </Box>
              )}
            </FormGroup>
          </FormControl>
        ) : (
          <Box sx={{ userSelect: "text" }}>
            {options.map((opt, idx) => {
              const value = optionValue(opt, idx);
              const letter = value.toUpperCase();
              const chosen = value === selectedValue;
              return (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    pl: 2,
                    borderRadius: 1,
                    bgcolor: greyBg,
                    border: chosen
                      ? `2px solid ${green}`
                      : `1px solid transparent`,
                    mb: 1.5,
                    "&:hover": { cursor: "pointer", bgcolor: "#eef0f3" },
                    userSelect: "text",
                    "& *": { userSelect: "text" },
                    pointerEvents: "auto",
                  }}
                  onClick={() => {
                    // Toggle: if clicking the same option, deselect it
                    const newValue = chosen ? "" : value;
                    handleAnswerChange?.(id, newValue);
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                      fontWeight: 700,
                      color: chosen ? "#fff" : "text.primary",
                      bgcolor: chosen ? green : "#fff",
                      border: `2px solid ${green}`,
                      userSelect: "none",
                    }}
                  >
                    {chosen ? (
                      <CheckIcon sx={{ fontSize: 18, color: "#fff" }} />
                    ) : (
                      letter
                    )}
                  </Box>
                  <HtmlRenderer
                    content={optionText(opt)}
                    component="span"
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.5,
                      userSelect: "text",
                      cursor: "text",
                      flex: 1,
                      pointerEvents: "auto",
                      fontSize: `calc((${zoomLevel / 100}) * ${typeof window !== 'undefined' && window.innerWidth < 600 ? '0.875rem' : '1rem'})`,
                    }}
                  />
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MockMcq;
